import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Bell, Send, Save, Search, Users, User } from 'lucide-react';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';

const STATUS_LABELS = {
  pending: 'Pending',
  pending_approval: 'Pending Approval',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  packed: 'Packed',
  ready: 'Ready',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  delayed: 'Delayed',
};

const STATUS_ORDER = Object.keys(STATUS_LABELS);

function statusBadgeClass(status) {
  const map = {
    pending: 'bg-yellow-100 text-yellow-800',
    pending_approval: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-indigo-100 text-indigo-800',
    packed: 'bg-purple-100 text-purple-800',
    ready: 'bg-green-100 text-green-800',
    out_for_delivery: 'bg-teal-100 text-teal-800',
    delivered: 'bg-green-100 text-green-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    delayed: 'bg-orange-100 text-orange-800',
  };
  return map[status] || 'bg-gray-100 text-gray-800';
}

// ── Tab 1: Message Templates ─────────────────────────────────────────────────

function TemplatesTab() {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState(null);

  const { data, isLoading } = useQuery(
    ['notification-templates'],
    () => api.get('/vendor/notifications/templates').then((r) => r.data.templates),
    { onSuccess: (data) => { if (!drafts) setDrafts({ ...data }); } }
  );

  const templates = drafts || data || {};

  const saveMutation = useMutation(
    (t) => api.put('/vendor/notifications/templates', { templates: t }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notification-templates']);
        showSuccessToast('Templates saved successfully');
      },
      onError: (e) => showErrorToast(e.response?.data?.message || 'Failed to save'),
    }
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        These messages appear in the push notification body when an order status changes.
        The title is always <span className="font-mono bg-gray-100 px-1 rounded text-xs">Order #ID</span>.
      </p>

      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Notification Message
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {STATUS_ORDER.map((status) => (
              <tr key={status}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusBadgeClass(status)}`}>
                    {STATUS_LABELS[status]}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <input
                    type="text"
                    value={templates[status] || ''}
                    onChange={(e) =>
                      setDrafts((prev) => ({ ...prev, [status]: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => saveMutation.mutate(templates)}
          disabled={saveMutation.isLoading}
          className="flex items-center space-x-2 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {saveMutation.isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          <span>{saveMutation.isLoading ? 'Saving...' : 'Save Templates'}</span>
        </button>
      </div>
    </div>
  );
}

// ── Tab 2: Send Notification ─────────────────────────────────────────────────

function SendTab() {
  const [form, setForm] = useState({ title: '', body: '' });
  const [recipient, setRecipient] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const { data: usersData, isLoading: usersLoading } = useQuery(
    ['customers-search', search],
    () => api.get('/vendor/notifications/customers', { params: { search } }).then((r) => r.data),
    { enabled: recipient === 'user' && search.length >= 2 }
  );

  const users = usersData?.customers || [];

  const sendMutation = useMutation(
    (payload) => api.post('/vendor/notifications/send', payload),
    {
      onSuccess: (res) => {
        showSuccessToast(res.data.message || 'Notification sent!');
        setForm({ title: '', body: '' });
        setSelectedUser(null);
        setSearch('');
      },
      onError: (e) => showErrorToast(e.response?.data?.message || 'Failed to send'),
    }
  );

  const handleSend = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      showErrorToast('Title and message are required');
      return;
    }
    if (recipient === 'user' && !selectedUser) {
      showErrorToast('Please select a user');
      return;
    }
    sendMutation.mutate({
      title: form.title,
      body: form.body,
      userId: recipient === 'user' ? selectedUser.id : undefined,
    });
  };

  return (
    <form onSubmit={handleSend} className="space-y-6 max-w-2xl">
      {/* Recipient */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Send To</label>
        <div className="flex space-x-6">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              name="recipient"
              value="all"
              checked={recipient === 'all'}
              onChange={() => { setRecipient('all'); setSelectedUser(null); }}
              className="h-4 w-4 text-primary-600"
            />
            <Users className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-700">All Customers</span>
          </label>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="radio"
              name="recipient"
              value="user"
              checked={recipient === 'user'}
              onChange={() => setRecipient('user')}
              className="h-4 w-4 text-primary-600"
            />
            <User className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-700">Specific Customer</span>
          </label>
        </div>
      </div>

      {/* User Search */}
      {recipient === 'user' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Search Customer</label>
          {selectedUser ? (
            <div className="flex items-center justify-between px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg">
              <div>
                <p className="text-sm font-medium text-primary-900">{selectedUser.name}</p>
                <p className="text-xs text-primary-600">{selectedUser.email || selectedUser.phone}</p>
              </div>
              <button
                type="button"
                onClick={() => { setSelectedUser(null); setSearch(''); }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email or phone..."
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {search.length >= 2 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {usersLoading ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500" />
                    </div>
                  ) : users.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-gray-500">No customers found</p>
                  ) : (
                    users.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => { setSelectedUser(u); setSearch(''); }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      >
                        <p className="text-sm font-medium text-gray-900">{u.name}</p>
                        <p className="text-xs text-gray-500">{u.email || u.phone || ''}</p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notification Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          placeholder="e.g. Special Offer Today!"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Body */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Message <span className="text-red-500">*</span>
        </label>
        <textarea
          rows={4}
          value={form.body}
          onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
          placeholder="Enter the notification message..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Preview */}
      {(form.title || form.body) && (
        <div className="bg-gray-900 rounded-2xl p-4 max-w-sm">
          <p className="text-xs text-gray-400 mb-2">Preview</p>
          <div className="bg-white rounded-xl p-3 shadow">
            <div className="flex items-start space-x-2">
              <div className="h-8 w-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Bell className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{form.title || 'Title'}</p>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{form.body || 'Message'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={sendMutation.isLoading}
          className="flex items-center space-x-2 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {sendMutation.isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          <span>
            {sendMutation.isLoading
              ? 'Sending...'
              : recipient === 'all'
              ? 'Send to All Customers'
              : 'Send to Customer'}
          </span>
        </button>
      </div>
    </form>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'templates', label: 'Message Templates', icon: Bell },
  { id: 'send', label: 'Send Notification', icon: Send },
];

const Notifications = () => {
  const [activeTab, setActiveTab] = useState('templates');

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Push Notifications</h2>

      <div className="border-b border-gray-200">
        <nav className="flex space-x-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'templates' ? <TemplatesTab /> : <SendTab />}
    </div>
  );
};

export default Notifications;
